// Contact field rules, shared so the panels and any future caller validate a
// phone or an email the same way.
//
// Country list comes from the app's own countryOptions rather than a list
// invented here, so it cannot drift from what Salesforce accepts. Every country
// currently sanctioned is +1 / NANP, which is why the length rule is a flat ten
// digits - matching the main form's minLength(10) / maxLength(10).
import { countryOptions } from "./options";

const DIAL_CODES = {
  "United States": "+1",
  Canada: "+1",
};

export const DEFAULT_PHONE_COUNTRY = "United States";

export function phoneCountryOptions() {
  return countryOptions
    .filter((option) => DIAL_CODES[option.value])
    .map((option) => ({
      value: option.value,
      text: `${option.text} (${DIAL_CODES[option.value]})`,
    }));
}

export function dialCodeFor(country) {
  return DIAL_CODES[country] || DIAL_CODES[DEFAULT_PHONE_COUNTRY];
}

// Strip everything the dealer may have typed for readability: spaces, dashes,
// parentheses, dots, and a leading +1 or 1.
export function normalizePhone(value) {
  if (value === null || value === undefined) return "";
  const digits = String(value).replace(/\D/g, "");
  return digits.length === 11 && digits.startsWith("1")
    ? digits.slice(1)
    : digits;
}

// What a person may reasonably type around the digits: spaces, dashes,
// parentheses, dots, and a leading +. Anything else is not formatting.
const PHONE_ALLOWED = /^[0-9\s().+-]*$/;

export function phoneError(value, { required = false } = {}) {
  const raw = value === null || value === undefined ? "" : String(value).trim();
  const digits = normalizePhone(value);

  if (!digits && !raw) {
    return required ? "Phone number is required" : null;
  }

  // Checked against the RAW value, not the digits. normalizePhone discards
  // every non-digit, so "631-555-2342dsa" reduces to ten valid digits and
  // passes every rule below - while the string that actually gets saved still
  // carries the letters. Salesforce takes it, and the number is unusable.
  if (!PHONE_ALLOWED.test(raw)) {
    return "Phone number can only contain digits";
  }

  if (!digits) {
    return required ? "Phone number is required" : null;
  }
  if (digits.length !== 10) {
    return "Phone number must be 10 digits";
  }
  // NANP: neither the area code nor the exchange may begin with 0 or 1.
  if (!/^[2-9]/.test(digits)) {
    return "Area code cannot start with 0 or 1";
  }
  if (!/^[2-9]/.test(digits.slice(3))) {
    return "Exchange cannot start with 0 or 1";
  }
  // N11 area codes (211, 411, 911 and friends) are service codes, not numbers.
  if (/^[2-9]11/.test(digits)) {
    return "That is a service code, not a phone number";
  }
  return null;
}

// Deliberately stricter than vuelidate's `email`, which accepts things like
// "a@b..c" and "a@b.c1". Rules, in order: exactly one @; a local part with no
// leading, trailing or doubled dot; a domain of dot-separated labels that are
// alphanumeric with inner hyphens; and a final label that is alphabetic and at
// least two characters, so no trailing digits or punctuation.
const LOCAL = /^[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+(\.[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+)*$/;
const DOMAIN_LABEL = /^[A-Za-z0-9]([A-Za-z0-9-]*[A-Za-z0-9])?$/;

export function emailError(value, { required = false } = {}) {
  const email = (value === null || value === undefined ? "" : String(value)).trim();

  if (!email) {
    return required ? "Email is required" : null;
  }
  if (/\s/.test(email)) {
    return "Email cannot contain spaces";
  }

  const parts = email.split("@");
  if (parts.length !== 2) {
    return "Email must contain a single @";
  }

  const [local, domain] = parts;
  if (!local || !LOCAL.test(local)) {
    return "The part before @ is not valid";
  }
  if (local.length > 64) {
    return "The part before @ is too long";
  }

  if (!domain || domain.includes("..")) {
    return "The domain is not valid";
  }

  const labels = domain.split(".");
  if (labels.length < 2) {
    return "The domain must include a dot, such as example.com";
  }
  if (!labels.every((label) => DOMAIN_LABEL.test(label))) {
    return "The domain is not valid";
  }

  const tld = labels[labels.length - 1];
  if (!/^[A-Za-z]{2,}$/.test(tld)) {
    return "The domain must end in letters, such as .com";
  }

  return null;
}

// --- vuelidate adapters -----------------------------------------------------
// The main form validates through vuelidate and the panels through the plain
// predicates above. Wrapping the predicates rather than writing a second rule
// keeps a single definition of "valid", which is the whole point of this file.
//
// Both treat an empty value as valid: `required` is what decides whether the
// field may be blank, and reporting two errors for one empty box is noise.
export function isValidEmail(value) {
  if (value === null || value === undefined || String(value).trim() === "") {
    return true;
  }
  return emailError(value) === null;
}

// The digit-count rules stay on the main form as they are; this adds only the
// NANP content rules it was missing - an area code or exchange starting 0 or 1,
// and N11 service codes such as 411 and 911, are not dialable numbers.
export function isValidNanpPhone(value) {
  if (value === null || value === undefined || String(value).trim() === "") {
    return true;
  }
  return phoneError(value) === null;
}

// The NANP content problems only - a number that is the right length but is
// not dialable. Length and non-digit problems are left out because the main
// form already has its own messages for those, and two errors on one box is
// noise. Returns null when there is nothing to say.
export function phoneAdvisory(value) {
  if (normalizePhone(value).length !== 10) return null;
  return phoneError(value);
}
